import { beginCheckoutReturnAttempt, claimCheckoutReturn } from './checkoutReturnState'
import { isCapacitorRuntime } from './mobileBootstrap'
import { getMemberMobileAuthBridge } from '@/members-portal/lib/memberMobileAuthBridge'

export function checkoutPlatform() { return isCapacitorRuntime() ? 'native' : 'web' }

// Reserve a web tab in the gesture, but never make its handle a prerequisite for
// creating checkout. Blocked web popups fall back to same-tab navigation.
let activeCheckout = null

export function prepareHostedCheckout(placeholder) {
  beginCheckoutReturnAttempt()
  const native = isCapacitorRuntime()
  let popup = null
  if (!native) { try { popup = window.open(placeholder, '_blank') } catch { /* Same-tab fallback below. */ } }
  const bridge = native ? getMemberMobileAuthBridge() : null
  let closed = false
  let opened = false
  let finishedListener
  const checkout = {
    get closed() { return closed || Boolean(popup?.closed) },
    finish() {
      closed = true
      finishedListener?.remove?.()
      if (activeCheckout === checkout) activeCheckout = null
    },
    close() {
      const shouldClose = native && opened && !closed
      checkout.finish()
      if (shouldClose) Promise.resolve(bridge.closeBrowser?.()).catch(() => {})
      popup?.close?.()
    },
    location: {
      async assign(url) {
        const destination = new URL(url)
        if (destination.protocol !== 'https:' || destination.hostname !== 'checkout.stripe.com') throw new Error('checkout_url_invalid')
        try {
          if (native) {
            finishedListener = await bridge.addBrowserFinishedListener?.(() => {
              if (closed) return
              checkout.finish()
              window.dispatchEvent(new CustomEvent('members:checkout-closed'))
            })
            activeCheckout = checkout
            await bridge.openBrowser(url)
            opened = true
          } else if (popup && !popup.closed) popup.location.assign(url)
          else window.location.assign(url)
        } catch {
          checkout.finish()
          popup?.close?.()
          console.warn('[members-checkout]', { boundary: 'browser_open', platform: native ? 'native' : 'web', code: 'open_failed' })
          throw new Error('checkout_open_failed')
        }
      },
    },
  }
  return checkout
}

// Claim synchronously before closing the browser: Browser.close can trigger
// foreground/finished events and React can reattach the launch URL listener.
export async function receiveNativeCheckoutReturn(rawUrl, clubCode, { bridge, navigate }) {
  const path = nativeCheckoutReturnPath(rawUrl, clubCode)
  if (!path) return false
  if (!claimCheckoutReturn(path)) return true
  activeCheckout?.finish()
  // Do not let a slow Browser.close delay unmounting the old recovery form.
  const close = Promise.resolve().then(() => bridge.closeBrowser?.()).catch(() => {})
  const url = new URL(path, 'https://members.privana.club')
  const cancelled = url.pathname.endsWith('/payment-cancelled') || ['payment', 'event_payment', 'status'].some(key => url.searchParams.get(key) === 'cancelled')
  // Keep a live booking form intact on cancellation, including its recovery key
  // and exact retry selections. Cold launches need the module return route.
  if (cancelled && activeReturnForm(url)) window.dispatchEvent(new CustomEvent('members:checkout-cancelled'))
  else navigate(path, { replace: true })
  await close
  return true
}

function activeReturnForm(url) {
  const current = window.location.pathname
  if (url.pathname.endsWith('/payment-cancelled')) return current === url.pathname.replace('/payment-cancelled', '/account')
  const club = url.pathname.split('/')[1]
  if (!current.startsWith(`/${club}/`)) return false
  const module = url.pathname.split('/')[2]
  return ['golf', 'book-racquet', 'spa', 'fitness'].includes(module) && current.startsWith(`/${club}/${module}/`) && !current.endsWith('/booking/success')
}

export function nativeCheckoutReturnPath(rawUrl, clubCode) {
  try {
    const url = new URL(rawUrl)
    if (url.protocol !== 'com.privana.members:' || url.hostname !== 'checkout') return null
    const path = url.searchParams.get('path')
    const target = new URL(path, 'https://members.privana.club')
    if (target.origin === 'https://members.privana.club' && /^\/guest\/event-payment\/[A-Za-z0-9_-]{24,}$/.test(target.pathname) && /^\d{6}$/.test(target.searchParams.get('club_code') || '')) return target.pathname + target.search
    if (target.origin !== 'https://members.privana.club' || !/^\d{6}$/.test(clubCode || '') || !target.pathname.startsWith(`/${clubCode}/`)) return null
    if (!/^\/\d{6}\/(?:payment-(?:received|cancelled)|(?:golf|fitness|spa|events|academy)|book-racquet\/(?:tennis|padel|squash|pickleball|badminton)\/book|(?:golf|fitness|spa)\/booking\/success|book-racquet\/(?:tennis|padel|squash|pickleball|badminton)\/booking\/success)$/.test(target.pathname)) return null
    return target.pathname + target.search
  } catch { return null }
}
